import { Controller } from "@hotwired/stimulus";

let CropperLib = null;

async function loadCropper() {
  if (CropperLib) return CropperLib;

  const module = await import("cropperjs");
  CropperLib = module.default || module;

  return CropperLib;
}

export default class CropperController extends Controller {
  static targets = ["input", "image", "profileData", "updateButton", "editButton"];

  connect() {
    this.cropper = null;
    this.objectUrl = null;
    this.originalImageUrl = null;
    this.cropData = null;

    this.modal = document.getElementById("cropModal");
    this.okButton = document.getElementById("okButton");
    this.cancelButton = document.getElementById("cancelButton");
    this.cropperImage = document.getElementById("cropperImage");

    this.cancelHandler = this.closeModal.bind(this);
    this.saveHandler = this.saveCroppedImage.bind(this);

    this.cancelButton?.addEventListener("click", this.cancelHandler);
    this.okButton?.addEventListener("click", this.saveHandler);
    this.originalImageUrl = null;
  }

  async getCropper() {
    return this.Cropper || (this.Cropper = await loadCropper());
  }

  async previewImage(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (this.hasEditButtonTarget) {
      this.editButtonTarget.classList.add("d-none");
    }

    await this.getCropper();

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }

    this.objectUrl = URL.createObjectURL(file);
    this.originalImageUrl = this.objectUrl;
    this.cropData = null;

    this.openModal(this.originalImageUrl);
  }

  openModal(imageUrl) {
    this.modal.classList.remove("d-none");
    this.modal.classList.add("d-block", "show");
    document.body.style.overflow = "hidden";

    this.cropperImage.onload = () => {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }

      this.createCropper();
    };

    this.cropperImage.src = imageUrl;
  }

  createCropper() {
    const isCover = this.element.dataset?.cover === "true";

    this.cropper = new this.Cropper(this.cropperImage, {
      viewMode: 1,
      dragMode: isCover ? "move" : "crop",
      cropBoxResizable: !isCover,
      zoomable: !isCover,
      zoomOnWheel: !isCover,
      scalable: !isCover,
      responsive: true,
      autoCropArea: 1,

      ready: () => {
        if (this.cropData) {
          this.cropper.setData(this.cropData);
          return;
        }

        const containerData = this.cropper.getContainerData();

        if (!isCover) {
          const cropWidth = 250;
          const cropHeight = 250;

          this.cropper.setCropBoxData({
            left: (containerData.width - cropWidth) / 2,
            top: (containerData.height - cropHeight) / 2,
            width: cropWidth,
            height: cropHeight,
          });

          return;
        }

        const cropWidth = containerData.width;
        const cropHeight = 250;

        this.cropper.setCropBoxData({
          left: 0,
          top: (containerData.height - cropHeight) / 2,
          width: cropWidth,
          height: cropHeight,
        });
      }
    });
  }

  saveCroppedImage() {
    if (!this.cropper) return;
    this.cropData = this.cropper.getData();

    const canvas = this.cropper.getCroppedCanvas({
      imageSmoothingQuality: "high",
    });

    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);

    this.imageTarget.src = dataUrl;
    this.profileDataTarget.value = dataUrl;
    this.element.dispatchEvent(
      new CustomEvent("cropper:image-changed", { bubbles: true })
    );

    if (this.hasEditButtonTarget) {
      this.editButtonTarget.classList.remove("d-none");
      this.editButtonTarget.classList.remove("disabled");
    }

    this.closeModal()
  }

  closeModal() {
    this.modal.classList.remove("show", "d-block");
    document.body.style.overflow = "auto";

    this.resetCropper();
  }

  resetCropper() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }

    if (this.objectUrl) {
      this.objectUrl = null;
    }

    if (this.cropperImage) {
      this.cropperImage.src = "";
      this.cropperImage.removeAttribute("src");
    }
  }

  restore() {
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }
  }

  disconnect() {
    this.cancelButton?.removeEventListener("click", this.cancelHandler);
    this.okButton?.removeEventListener("click", this.saveHandler);
    this.resetCropper();
  }

  async editImage() {
    if (!this.originalImageUrl && !this.hasImageTarget) return;

    await this.getCropper();

    // For an existing moment, use its original image until a new one is selected.
    const imageUrl = this.originalImageUrl || this.imageTarget.src;

    if (!imageUrl) return;

    this.openModal(imageUrl);
  }
}
